
import React, { useEffect, useState } from "react";
import { fetchChangeLogs } from "@/utils/admin";
import { useAuth } from "@/providers/AuthProvider";
import { isAdmin } from "@/utils/admin";
import MainNav from "@/components/MainNav";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { FileDown, Filter, RefreshCw } from "lucide-react";

interface ChangeLogRow {
  id: string;
  component_type: string;
  component_name: string;
  action: string;
  description: string;
  details: any;
  created_by: string | null;
  created_at: string;
}

const ChangeLogsPage: React.FC = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<ChangeLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10;
  
  // Filter states
  const [componentTypeFilter, setComponentTypeFilter] = useState<string>("all");
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [fromDateFilter, setFromDateFilter] = useState<string>("");
  const [toDateFilter, setToDateFilter] = useState<string>("");

  // Unique filter options from data
  const [componentTypes, setComponentTypes] = useState<string[]>([]);
  const [actionTypes, setActionTypes] = useState<string[]>([]);

  useEffect(() => {
    if (user) {
      isAdmin(user.id).then(admin => {
        setIsAdminUser(admin);
        if (admin) {
          fetchLogs();
        }
      });
    }
  }, [user]);

  const fetchLogs = async () => {
    setLoading(true);
    const { data, error } = await fetchChangeLogs({
      limit: pageSize,
      offset: (page - 1) * pageSize,
      componentType: componentTypeFilter !== "all" ? componentTypeFilter : undefined,
      actionType: actionFilter !== "all" ? actionFilter : undefined,
      fromDate: fromDateFilter || undefined,
      toDate: toDateFilter || undefined
    });

    if (!error && data) {
      setLogs(data);
      // Extract unique filter options
      const types = [...new Set(data.map(log => log.component_type))];
      const actions = [...new Set(data.map(log => log.action))];
      
      setComponentTypes(prev => {
        const combined = [...new Set([...prev, ...types])];
        return combined;
      });
      
      setActionTypes(prev => {
        const combined = [...new Set([...prev, ...actions])];
        return combined;
      });
      
      // Approximately calculate total pages
      // In a real app, you would fetch the count separately
      if (data.length < pageSize && page === 1) {
        setTotalPages(1);
      } else if (data.length < pageSize) {
        setTotalPages(page);
      } else {
        setTotalPages(page + 1);
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAdminUser) {
      fetchLogs();
    }
  }, [page, isAdminUser, componentTypeFilter, actionFilter, fromDateFilter, toDateFilter]);

  const exportLogs = () => {
    // Create CSV from logs
    const headers = ["Type", "Component", "Action", "Description", "Created By", "Created At"];
    const csvRows = [
      headers.join(","),
      ...logs.map(log => [
        log.component_type,
        log.component_name,
        log.action,
        `"${log.description.replace(/"/g, '""')}"`,
        log.created_by || "--",
        new Date(log.created_at).toLocaleString()
      ].join(","))
    ];
    
    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `change_logs_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetFilters = () => {
    setComponentTypeFilter("all");
    setActionFilter("all");
    setFromDateFilter("");
    setToDateFilter("");
    setPage(1);
  };

  if (!user) return (
    <>
      <MainNav currentPath="/admin/logs" />
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="text-center">You must be logged in to access this page.</div>
      </div>
    </>
  );

  if (!isAdminUser) return (
    <>
      <MainNav currentPath="/admin/logs" />
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="text-center">You don't have permission to access this page.</div>
      </div>
    </>
  );

  return (
    <>
      <MainNav currentPath="/admin/logs" />
      <div className="max-w-7xl mx-auto px-4 py-10">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-2xl font-bold">Change Logs</CardTitle>
            <div className="flex space-x-2">
              <Button variant="outline" size="sm" onClick={resetFilters}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reset Filters
              </Button>
              <Button variant="outline" size="sm" onClick={exportLogs}>
                <FileDown className="h-4 w-4 mr-2" />
                Export Logs
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Component Type</label>
                <Select value={componentTypeFilter} onValueChange={setComponentTypeFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {componentTypes.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Action</label>
                <Select value={actionFilter} onValueChange={setActionFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    {actionTypes.map(action => (
                      <SelectItem key={action} value={action}>{action}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">From Date</label>
                <Input 
                  type="date" 
                  value={fromDateFilter}
                  onChange={e => setFromDateFilter(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">To Date</label>
                <Input 
                  type="date" 
                  value={toDateFilter}
                  onChange={e => setToDateFilter(e.target.value)}
                />
              </div>
            </div>
            
            {loading ? (
              <div className="py-20 text-center">Loading change logs...</div>
            ) : logs.length === 0 ? (
              <div className="py-20 text-center">No change logs found matching your criteria.</div>
            ) : (
              <>
                <div className="rounded-md border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Component</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>By</TableHead>
                        <TableHead>At</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="font-medium">{log.component_name}</TableCell>
                          <TableCell>{log.component_type}</TableCell>
                          <TableCell>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              log.action === 'create' ? 'bg-green-100 text-green-800' :
                              log.action === 'update' ? 'bg-blue-100 text-blue-800' :
                              log.action === 'delete' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {log.action}
                            </span>
                          </TableCell>
                          <TableCell className="max-w-xs truncate">{log.description}</TableCell>
                          <TableCell>{log.created_by?.slice(0, 8) || "--"}</TableCell>
                          <TableCell>{new Date(log.created_at).toLocaleString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                <Pagination className="mt-6">
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => page > 1 && setPage(page - 1)}
                        className={page === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                    
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      // Calculate page numbers to show
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      
                      return (
                        <PaginationItem key={i}>
                          <PaginationLink
                            onClick={() => setPage(pageNum)}
                            isActive={pageNum === page}
                          >
                            {pageNum}
                          </PaginationLink>
                        </PaginationItem>
                      );
                    })}
                    
                    {totalPages > 5 && page < totalPages - 2 && (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    )}
                    
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => page < totalPages && setPage(page + 1)}
                        className={page === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default ChangeLogsPage;
